/* eslint-disable react/no-array-index-key */
import { Tooltip } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';
import IntelligentQA from './components/IntelligentQA';
import TreatmentRecommendation from './components/TreatmentRecommendation';
import styles from './index.less';

/**
 * 治疗方案主组件属性接口
 */
interface TreatmentPlanProps {
  calcHeight?: number;
  /**
   * 患者ID
   */
  patientId?: string;
  /**
   * 显示模式
   * @default 'nested' - 嵌套模式（默认），内容嵌套在父级内容中
   * 'drawer' - 抽屉模式，内容浮在父级内容之上
   */
  mode?: 'nested' | 'drawer';
}

/**
 * 治疗方案主组件
 * 整合治疗方案推荐和智能问答功能的容器组件
 */
const TreatmentPlan = forwardRef<HTMLDivElement, TreatmentPlanProps>(
  ({ calcHeight = 84, patientId, mode = 'nested' }, ref) => {
    // 控制MDT助手菜单的显示状态
    const [showMenu, setShowMenu] = useState(false);
    // 当前选中的内容类型：treatment(治疗方案推荐) 或 qa(病例智能问答)
    const [activeContent, setActiveContent] = useState<'treatment' | 'qa' | undefined>(undefined);
    // 控制MDT助手位置
    const [mdtHelperPosition, setMdtHelperPosition] = useState<'right' | 'left'>('right');

    // 监听activeContent变化，更新MDT助手位置
    useEffect(() => {
      if (activeContent) {
        setMdtHelperPosition('left');
      } else {
        setMdtHelperPosition('right');
      }
    }, [activeContent]);

    // 切换MDT助手菜单显示状态
    const toggleMenu = () => {
      setShowMenu(!showMenu);
      // // 如果关闭菜单，同时清除当前选中的内容
      if (showMenu) {
        setActiveContent(undefined);
      } else {
        setActiveContent('treatment');
      }
    };

    // 点击菜单项
    const handleMenuClick = (type: 'treatment' | 'qa') => {
      setActiveContent(type);
    };

    // 渲染内容区
    const renderContent = () => {
      return (
        <>
          <TreatmentRecommendation
            planType={activeContent}
            patientId={patientId}
            style={{ display: activeContent === 'treatment' ? 'flex' : 'none' }}
            onClose={toggleMenu}
          />
          <IntelligentQA
            planType={activeContent}
            patientId={patientId}
            style={{ display: activeContent === 'qa' ? 'flex' : 'none' }}
            onClose={toggleMenu}
          />
        </>
      );
    };

    return (
      <div
        ref={ref}
        className={classNames(styles.treatmentPlan, styles[`mode-${mode}`])}
        style={{ '--height': `${calcHeight}px` } as React.CSSProperties}
      >
        {/* MDT助手按钮 */}
        <div className={classNames(styles.mdtHelper, styles[mdtHelperPosition])}>
          <Tooltip title="iMDT助手" placement="left">
            <div className={classNames(styles.mdtButton)} onClick={toggleMenu}>
              {/* MDT助手
            {mdtHelperPosition === 'right' ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />} */}
            </div>
          </Tooltip>

          {/* 菜单选项 */}
          <div
            className={classNames(styles.menuOptions, { [styles.menuOptionsVisible]: showMenu })}
          >
            <Tooltip title="治疗方案推荐" placement="left">
              <div
                className={classNames(
                  styles.menuItem,
                  {
                    [styles.active]: activeContent === 'treatment',
                  },
                  'iconfont icon-zhiliaofangantuijian',
                )}
                onClick={() => handleMenuClick('treatment')}
              />
            </Tooltip>
            <Tooltip title="病例智能问答" placement="left">
              <div
                className={classNames(
                  styles.menuItem,
                  { [styles.active]: activeContent === 'qa' },
                  'iconfont icon-binglizhinengwenda',
                )}
                onClick={() => handleMenuClick('qa')}
              />
            </Tooltip>
          </div>
        </div>

        {/* 抽屉模式的遮罩层 */}
        {/* {mode === 'drawer' && activeContent && (
        <div
          className={classNames(styles.drawerMask, { [styles.maskVisible]: activeContent })}
          onClick={() => setActiveContent(undefined)}
        />
      )} */}
        <div className={classNames(styles.contentArea, { [styles.contentVisible]: activeContent })}>
          {renderContent()}
        </div>
      </div>
    );
  },
);

export default TreatmentPlan;
